/**
 * Development Status Registry
 *
 * Maps route paths to their data-source maturity level.
 * Used by sidebar and page header to display development progress indicators.
 *
 * LIVE    — Real ClickHouse data via edge-api
 * FIXTURE — Server returns fixture JSON (appPageData)
 * MOCK    — Frontend hardcoded mock data
 * STATIC  — No data fetching needed (pure UI/navigation)
 */

export type DevStatus = "live" | "fixture" | "mock" | "static";

export interface DevStatusMeta {
  label: string;
  shortLabel: string;
  description: string;
  dotClass: string;
  badgeClass: string;
}

export const DEV_STATUS_META: Record<DevStatus, DevStatusMeta> = {
  live: {
    label: "Live Data",
    shortLabel: "Live",
    description: "실제 데이터 연동 완료",
    dotClass: "bg-emerald-500",
    badgeClass: "bg-emerald-500/10 text-emerald-700 ring-emerald-500/20",
  },
  fixture: {
    label: "Fixture Data",
    shortLabel: "Fixture",
    description: "서버 fixture 데이터 사용 중 — 실데이터 전환 필요",
    dotClass: "bg-amber-500",
    badgeClass: "bg-amber-500/10 text-amber-700 ring-amber-500/20",
  },
  mock: {
    label: "Mock Data",
    shortLabel: "Mock",
    description: "프론트엔드 하드코딩 데이터 — API 연동 필요",
    dotClass: "bg-rose-500",
    badgeClass: "bg-rose-500/10 text-rose-700 ring-rose-500/20",
  },
  static: {
    label: "Static",
    shortLabel: "Static",
    description: "데이터 연동 불필요 (순수 UI)",
    dotClass: "bg-slate-400",
    badgeClass: "bg-slate-400/10 text-slate-600 ring-slate-400/20",
  },
};

const STATUS_MAP: Record<string, DevStatus> = {
  /* ═══ LIVE ═══ */
  // Phase A audit note (20260418): `/dashboard` and `/channels/explorer` are
  // marked `live` overall but contain partial-stub sub-widgets
  // (activity feed uses STUB-ONLY `getDashboardActivity`; smartplace tab uses
  // STUB-ONLY `listSmartPlaces`). Tracked in Phase D backlog — backend CH
  // implementation needed for both methods.
  "/dashboard": "live",
  "/keywords": "live",
  "/keywords/data": "live",
  "/keywords/explorer/list": "live",
  "/keywords/data/stats": "live",
  "/keywords/data/rankings": "live",
  "/keywords/data/trends": "live",
  "/keywords/details": "live",
  "/keywords/analysis/clusters": "live",
  "/keywords/tracking/alerts": "live",
  "/keywords/serp/suggestions": "live",
  "/keywords/serp/explorer": "live",
  "/search-marketing/keywords/explorer": "live",
  "/channels/explorer": "live",
  "/contents/explorer": "live",
  "/contents/analysis/summary": "live",
  "/insights/anomalies/detection": "live",
  "/chat": "live",
  "/member/mypage": "live",
  "/member/checkout": "live",
  "/member/checkout/callback": "live",
  "/search-marketing": "live",
  "/keywords/serp/layout": "live",

  /* ═══ FIXTURE ═══ */
  // Phase A audit (20260418): resolves through STUB-ONLY backend methods.
  // Moved from `live` after static audit confirmed appPageData usage.
  "/keywords/analysis/gap": "fixture",
  "/keywords/analysis/intent": "fixture",
  "/keywords/serp/benchmark": "fixture",
  "/channels/diagnostics/compare": "fixture",
  "/channels/diagnostics/health": "fixture",
  "/channels/diagnostics/consulting": "fixture",
  "/channels/proposals": "fixture",
  "/contents/workspace/optimize": "fixture",
  "/contents/analysis/trend-match": "fixture",
  "/contents/workspace/draft": "fixture",
  "/contents/workspace/draft/review": "fixture",
  "/search-marketing/optimization/bid-strategy": "fixture",
  "/search-marketing/workspace/copy-generator": "fixture",

  /* ═══ MOCK ═══ */
  "/keywords/tracking/positions": "mock",
  "/channels/diagnostics/forecast": "mock",
  "/contents/workspace/write-post": "live",
  "/contents/workspace/brand-voice": "live",
  "/contents/workspace/calendar": "mock",
  "/search-marketing/campaigns/builder": "mock",
  "/search-marketing/optimization/roi-simulator": "mock",
  "/insights/anomalies/competitors": "mock",
  "/insights/quality/low-quality": "mock",

  /* ═══ STATIC ═══ */
  "/contents": "static",
  "/contents/analysis": "static",
  "/contents/workspace": "static",
  "/channels/diagnostics": "static",
  "/keywords/analysis": "static",
  "/keywords/tracking": "static",
  "/keywords/serp": "static",
  "/insights": "static",
  "/insights/anomalies": "static",
  "/search-marketing/optimization": "static",
  "/help": "static",
};

/**
 * Look up dev status for a route path.
 * Uses prefix matching for dynamic routes (e.g. /keywords/details/$id).
 */
export function getDevStatus(path: string): DevStatus | null {
  // Exact match first
  if (STATUS_MAP[path]) return STATUS_MAP[path];

  // Prefix match for dynamic routes and nested paths
  const normalized = path.replace(/\/+$/, "");
  for (const [prefix, status] of Object.entries(STATUS_MAP)) {
    if (normalized.startsWith(prefix + "/") || normalized === prefix) {
      return status;
    }
  }

  return null;
}
